import type { ResponseMetadata } from '@delivery-metrics/shared';
import { formatDistance } from 'date-fns';

/**
 * How the dashboard should present the freshness of what it is showing (US-23).
 *
 * `partial` is the state this story exists for. `freshThrough` is the **oldest**
 * contributing source, not the newest, so a collector cycle in which one
 * programme or source failed leaves the rollup genuinely current in some
 * dimensions and behind in others. Presenting that as `fresh` would under-report
 * silently, which is the failure mode the programme defends against in four
 * independent places; this is the one the user actually sees.
 */
export type FreshnessSeverity = 'unknown' | 'stale' | 'partial' | 'fresh';

export interface FreshnessDescription {
  severity: FreshnessSeverity;
  /** Short form, for the chip. */
  label: string;
  /** The whole story, for the tooltip. */
  detail: string;
}

const parse = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ago = (value: Date, now: Date): string =>
  formatDistance(value, now, { addSuffix: true });

const plural = (count: number): string => (count === 1 ? 'source' : 'sources');

const failedSourceCount = (metadata: ResponseMetadata): string => {
  const failed = metadata.collectLegsFailed ?? 0;
  const total = metadata.collectLegsTotal;
  // Pluralise on the total when there is one: "1 of 8 sources", not
  // "1 of 8 source".
  return total
    ? `${failed} of ${total} ${plural(total)}`
    : `${failed} ${plural(failed)}`;
};

const completeThrough = (freshThrough: Date | null, now: Date): string =>
  freshThrough
    ? `Complete only through ${ago(freshThrough, now)}.`
    : 'How far the data is complete through is not known.';

/**
 * Turn a v2 response's `metadata` into what the UI should say about it.
 *
 * `now` is a parameter rather than a call to `Date.now()` so the wording is
 * testable without freezing the clock.
 */
export function describeFreshness(
  metadata: ResponseMetadata | null | undefined,
  now: Date = new Date()
): FreshnessDescription {
  if (!metadata) {
    return {
      severity: 'unknown',
      label: 'Freshness unknown',
      detail:
        'The dashboard could not read when this data was last built, so it ' +
        'cannot tell you how current these figures are.',
    };
  }

  const builtAt = parse(metadata.builtAt);
  const freshThrough = parse(metadata.freshThrough);

  if (!builtAt) {
    return {
      severity: 'unknown',
      label: 'Never built',
      detail:
        'No rollup has completed in this environment yet, so these figures ' +
        'have no completed build behind them.',
    };
  }

  const failed = metadata.collectLegsFailed ?? 0;
  const partialNote =
    failed > 0
      ? ` Collection failed for ${failedSourceCount(metadata)} in the last cycle.`
      : '';

  if (metadata.stale) {
    return {
      severity: 'stale',
      label: 'Data stale',
      detail:
        `A rebuild has failed since the last successful one, so this is not ` +
        `the latest data. Last successful build ${ago(builtAt, now)}. ` +
        completeThrough(freshThrough, now) +
        partialNote,
    };
  }

  if (failed > 0) {
    return {
      severity: 'partial',
      label: `Partially stale — ${failedSourceCount(metadata)} failed`,
      detail:
        `The last rebuild succeeded ${ago(builtAt, now)}, but collection ` +
        `failed for ${failedSourceCount(metadata)}, so some figures are ` +
        `behind the others. ` +
        completeThrough(freshThrough, now),
    };
  }

  return {
    severity: 'fresh',
    label: `Updated ${ago(freshThrough ?? builtAt, now)}`,
    detail:
      `Built ${ago(builtAt, now)} from a complete collection cycle. ` +
      completeThrough(freshThrough, now),
  };
}
